import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import NavBar from "../components/NavBar";
import API from "../api";
import { useAuth } from "../context/AuthContext";
import { Plus, Radio, ExternalLink, Trash2, Copy, X, Play, Calendar } from "lucide-react";

export default function MatchesDashboard() {
  const [matches, setMatches] = useState([]);
  const [teams, setTeams] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    name: "", team1_id: "", team2_id: "", tournament_id: "",
    team1_full: "INDIA", team1_short: "IND", team2_full: "BANGLADESH", team2_short: "BAN",
    total_overs: 20, venue: "",
  });
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState("");
  const { user } = useAuth();
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, t, tour] = await Promise.all([
        API.get("/matches"), API.get("/teams"), API.get("/tournaments"),
      ]);
      setMatches(m.data); setTeams(t.data); setTournaments(tour.data);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const res = await API.post("/matches", form);
      setShowCreate(false);
      navigate(`/controller/${res.data.match_id}`);
    } catch (e2) { setErr(e2.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const remove = async (mid) => {
    if (!window.confirm("Delete this match permanently?")) return;
    await API.delete(`/matches/${mid}`); await load();
  };

  const overlayUrl = (mid) => `${window.location.origin}/overlay/${mid}`;

  const copyOverlay = async (mid) => {
    const url = overlayUrl(mid);
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const ta = document.createElement("textarea");
        ta.value = url; document.body.appendChild(ta);
        ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
      }
      setCopied(mid); setTimeout(() => setCopied(""), 1500);
    } catch (e) {
      window.prompt("Copy this Overlay URL:", url);
    }
  };

  const daysLeft = user?.expiry_date ? Math.ceil((new Date(user.expiry_date) - new Date()) / 86400000) : null;

  return (
    <div className="min-h-screen bg-slate-950">
      <NavBar subtitle="My Matches" />
      <main className="pt-24 pb-16 max-w-7xl mx-auto px-6">
        {daysLeft !== null && (
          <div data-testid="expiry-banner" className={`mb-6 p-4 rounded-xl border flex items-center justify-between ${
            daysLeft <= 3 ? "bg-red-500/10 border-red-800 text-red-300"
            : daysLeft <= 7 ? "bg-amber-500/10 border-amber-800 text-amber-300"
            : "bg-emerald-500/10 border-emerald-800 text-emerald-300"
          }`}>
            <div className="flex items-center gap-3">
              <Calendar size={18}/>
              <div>
                <div className="font-semibold">Subscription {daysLeft > 0 ? "Active" : "Expired"}</div>
                <div className="text-sm opacity-80">
                  {daysLeft > 0 ? `${daysLeft} days remaining · expires ${new Date(user.expiry_date).toLocaleDateString()}` : `Expired on ${new Date(user.expiry_date).toLocaleDateString()}`}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <h1 className="font-heading text-4xl font-extrabold uppercase tracking-tight">My Matches</h1>
            <p className="text-slate-400 mt-1">Create, load and control your live broadcasts</p>
          </div>
          <button onClick={() => setShowCreate(true)} data-testid="create-match-button"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 font-semibold">
            <Plus size={18} /> New Match
          </button>
        </div>

        {loading ? <div className="p-10 text-center text-slate-400">Loading...</div> :
          matches.length === 0 ? (
            <div className="glass rounded-2xl border border-slate-800 p-12 text-center">
              <Radio className="mx-auto text-blue-500 mb-4" size={40}/>
              <div className="font-heading text-2xl font-bold uppercase">No matches yet</div>
              <p className="text-slate-400 mt-2">Create your first match to start scoring live graphics.</p>
              <button onClick={() => setShowCreate(true)} className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 font-semibold">
                <Plus size={16}/> Create First Match
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {matches.map(m => (
                <div key={m.match_id} data-testid={`match-card-${m.match_id}`} className="glass rounded-xl border border-slate-800 p-5 hover:border-slate-600 transition">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-heading text-xl font-extrabold uppercase">{m.name}</div>
                      <div className="text-sm text-slate-400 mt-0.5">{m.team1_full} vs {m.team2_full}</div>
                    </div>
                    <button onClick={() => remove(m.match_id)} data-testid={`match-delete-${m.match_id}`} className="p-1.5 rounded bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-900"><Trash2 size={14}/></button>
                  </div>
                  <div className="mt-3 text-xs text-slate-500 flex gap-4">
                    <span>{m.total_overs} overs</span>{m.venue && <span>{m.venue}</span>}
                  </div>
                  <div className="mt-5 flex flex-col gap-2">
                    <Link to={`/controller/${m.match_id}`} data-testid={`match-load-${m.match_id}`}
                      className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 font-semibold text-sm">
                      <Play size={14}/> Open Controller
                    </Link>
                    <div className="flex gap-2">
                      <button onClick={() => copyOverlay(m.match_id)} data-testid={`match-copy-overlay-${m.match_id}`}
                        className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-sm border border-slate-700">
                        <Copy size={14}/> {copied === m.match_id ? "Copied!" : "Copy Overlay URL"}
                      </button>
                      <a href={overlayUrl(m.match_id)} target="_blank" rel="noreferrer" data-testid={`match-open-overlay-${m.match_id}`}
                        className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-sm border border-slate-700">
                        <ExternalLink size={14}/>
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
        }
      </main>

      {showCreate && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="glass rounded-2xl border border-slate-700 w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-2xl font-extrabold uppercase">New Match</h2>
              <button onClick={() => setShowCreate(false)} className="p-1 hover:bg-slate-800 rounded"><X size={18}/></button>
            </div>
            <form onSubmit={create} className="space-y-3">
              <input required value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Match Name (e.g., 1st T20I)" data-testid="create-match-name" className="w-full px-3 py-2 rounded bg-slate-800 border border-slate-700"/>

              {tournaments.length > 0 && (
                <div>
                  <label className="text-xs uppercase text-slate-400 font-semibold">Tournament (optional)</label>
                  <select value={form.tournament_id} onChange={e=>setForm({...form,tournament_id:e.target.value})} data-testid="create-match-tournament"
                    className="w-full px-3 py-2 rounded bg-slate-800 border border-slate-700">
                    <option value="">— None —</option>
                    {tournaments.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase text-slate-400 font-semibold">Team 1</label>
                  {teams.length > 0 ? (
                    <select value={form.team1_id} onChange={e=>{
                      const t = teams.find(x=>x.id===e.target.value);
                      setForm({...form, team1_id: e.target.value, team1_full: t?.name || form.team1_full, team1_short: t?.short_name || form.team1_short});
                    }} data-testid="create-match-team1-select"
                      className="w-full px-3 py-2 rounded bg-slate-800 border border-slate-700">
                      <option value="">— Type manually —</option>
                      {teams.map(t => <option key={t.id} value={t.id}>{t.name} ({t.short_name})</option>)}
                    </select>
                  ) : (
                    <div className="text-xs text-slate-500 py-2">No saved teams. Add some in <b>Teams Library</b>.</div>
                  )}
                </div>
                <div>
                  <label className="text-xs uppercase text-slate-400 font-semibold">Team 2</label>
                  {teams.length > 0 ? (
                    <select value={form.team2_id} onChange={e=>{
                      const t = teams.find(x=>x.id===e.target.value);
                      setForm({...form, team2_id: e.target.value, team2_full: t?.name || form.team2_full, team2_short: t?.short_name || form.team2_short});
                    }} data-testid="create-match-team2-select"
                      className="w-full px-3 py-2 rounded bg-slate-800 border border-slate-700">
                      <option value="">— Type manually —</option>
                      {teams.map(t => <option key={t.id} value={t.id}>{t.name} ({t.short_name})</option>)}
                    </select>
                  ) : null}
                </div>
              </div>

              {(!form.team1_id || !form.team2_id) && (
                <div className="grid grid-cols-2 gap-3">
                  {!form.team1_id && <>
                    <input required value={form.team1_full} onChange={e => setForm({...form, team1_full: e.target.value})} placeholder="Team 1" data-testid="create-match-t1-full" className="px-3 py-2 rounded bg-slate-800 border border-slate-700"/>
                    <input required value={form.team1_short} onChange={e => setForm({...form, team1_short: e.target.value})} placeholder="T1 Short" data-testid="create-match-t1-short" className="px-3 py-2 rounded bg-slate-800 border border-slate-700"/>
                  </>}
                  {!form.team2_id && <>
                    <input required value={form.team2_full} onChange={e => setForm({...form, team2_full: e.target.value})} placeholder="Team 2" data-testid="create-match-t2-full" className="px-3 py-2 rounded bg-slate-800 border border-slate-700"/>
                    <input required value={form.team2_short} onChange={e => setForm({...form, team2_short: e.target.value})} placeholder="T2 Short" data-testid="create-match-t2-short" className="px-3 py-2 rounded bg-slate-800 border border-slate-700"/>
                  </>}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <input required type="number" min="1" max="50" value={form.total_overs} onChange={e => setForm({...form, total_overs: parseInt(e.target.value) || 20})} placeholder="Overs" data-testid="create-match-overs" className="px-3 py-2 rounded bg-slate-800 border border-slate-700"/>
                <input value={form.venue} onChange={e => setForm({...form, venue: e.target.value})} placeholder="Venue" data-testid="create-match-venue" className="px-3 py-2 rounded bg-slate-800 border border-slate-700"/>
              </div>
              {err && <div className="text-sm p-2 rounded bg-red-500/10 border border-red-900 text-red-300">{err}</div>}
              <button type="submit" disabled={busy} data-testid="create-match-submit" className="w-full px-4 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-60 font-semibold">
                {busy ? "Creating..." : "Create & Open"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
